import {
  BodyCorporateDetails,
  BodyCorporateType,
  CommunityTitlesSchemeDetails,
  SurveyPlanUploadModel,
  UploadedDocument,
} from '@urbanx/agx-ui-components';
import { PoolSafetyFileType } from '@urbanx/agx.campaigns.client';

export interface SellerDisclosureComplianceValues {
  allVendorsOrSolicitorSigned: boolean;
  surveyPlan?: SurveyPlanUploadModel;
  poolSafetyFileType?: PoolSafetyFileType;
  poolSafetyAttachedToSellerDisclosure?: boolean;
  poolSafetyCertificatePDF?: UploadedDocument[];
  buildingCertificatePDF?: UploadedDocument[];
  poolSafetyForm36PDF?: UploadedDocument[];
  communityTitlesSchemeDetails?: CommunityTitlesSchemeDetails;
  bodyCorporateDetails?: BodyCorporateDetails;
}

const hasDocuments = (documents?: UploadedDocument[]) => (documents?.length ?? 0) > 0;

export const isSellerDisclosureComplianceComplete = ({
  allVendorsOrSolicitorSigned,
  surveyPlan,
  poolSafetyFileType = PoolSafetyFileType.NoPool,
  poolSafetyAttachedToSellerDisclosure,
  poolSafetyCertificatePDF,
  buildingCertificatePDF,
  poolSafetyForm36PDF,
  communityTitlesSchemeDetails,
  bodyCorporateDetails,
}: SellerDisclosureComplianceValues) => {
  const surveyPlanComplete =
    surveyPlan?.surveyPlansAttachedToSellerDisclosure === true ||
    hasDocuments(surveyPlan?.surveyPlans);

  const poolSafetyComplete = (() => {
    if (poolSafetyFileType === PoolSafetyFileType.NoPool) return true;
    if (poolSafetyAttachedToSellerDisclosure) return true;
    if (poolSafetyFileType === PoolSafetyFileType.PoolSafetyCertificate)
      return hasDocuments(poolSafetyCertificatePDF);
    if (poolSafetyFileType === PoolSafetyFileType.BuildingCertificate)
      return hasDocuments(buildingCertificatePDF);
    if (poolSafetyFileType === PoolSafetyFileType.Form36) return hasDocuments(poolSafetyForm36PDF);
    return false;
  })();

  const communityTitlesComplete =
    communityTitlesSchemeDetails?.isCommunityTitlesScheme === false ||
    (communityTitlesSchemeDetails?.isCommunityTitlesScheme === true &&
      (communityTitlesSchemeDetails.statementsAttachedToSellerDisclosure === true ||
        (Boolean(communityTitlesSchemeDetails.schemeName?.trim()) &&
          Boolean(communityTitlesSchemeDetails.schemeNumber?.trim()) &&
          hasDocuments(communityTitlesSchemeDetails.communityManagementStatements))));

  const bodyCorporateComplete = (() => {
    const type = bodyCorporateDetails?.bodyCorporateType;
    if (type === BodyCorporateType.None) return true;
    if (!type) return false;
    if (type === BodyCorporateType.CompanyManaged && !bodyCorporateDetails.companyName?.trim())
      return false;
    return (
      bodyCorporateDetails.certificateAttachedToSellerDisclosure === true ||
      Boolean(bodyCorporateDetails.bodyCorporateCertificate)
    );
  })();

  return (
    allVendorsOrSolicitorSigned &&
    surveyPlanComplete &&
    poolSafetyComplete &&
    communityTitlesComplete &&
    bodyCorporateComplete
  );
};
